import { useId, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PageV3 } from '@docgen/schema';
import { Button, ErrorNotice, Field, Modal, SegmentedControl, TextArea, TextInput } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  PAGE_LIBRARY_DESCRIPTION_MAX,
  PAGE_LIBRARY_NAME_MAX,
  PAGE_LIBRARY_QUERY_KEY,
  PAGE_LIBRARY_VISIBILITY,
  type LibraryDraft,
  type LibraryItemSummary,
  type LibrarySuggestion,
  type PageLibraryVisibility,
} from '../lib/page-library';
import { librarySummaryLine } from '../lib/library-pages';

export interface SaveToLibraryDialogProps {
  /** The pages being saved, in document order. */
  readonly pages: readonly PageV3[];
  /** Saves the proposal if it has unsaved changes, then saves the pages to the library. */
  readonly onSave: (draft: LibraryDraft) => Promise<LibraryItemSummary>;
  /** Saves pending edits, then asks the API to draft a name and description from these pages. */
  readonly onSuggest: () => Promise<LibrarySuggestion>;
  readonly onClose: () => void;
}

const VISIBILITY_OPTIONS = [
  { key: PAGE_LIBRARY_VISIBILITY.private, label: 'Only me' },
  { key: PAGE_LIBRARY_VISIBILITY.team, label: 'Whole team' },
] as const;

/**
 * Save to library: one short form (name, description, who can see it) that
 * becomes its own confirmation once saved. Mount it only while open, so each
 * opening starts from a clean form.
 */
export function SaveToLibraryDialog({ pages, onSave, onSuggest, onClose }: SaveToLibraryDialogProps): React.ReactElement {
  const nameId = useId();
  const descriptionId = useId();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<PageLibraryVisibility>(PAGE_LIBRARY_VISIBILITY.private);
  const [showNameError, setShowNameError] = useState(false);

  const save = useMutation({
    mutationFn: () => onSave({ name: name.trim(), description: description.trim(), visibility }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PAGE_LIBRARY_QUERY_KEY }),
  });

  const submit = (): void => {
    if (name.trim() === '') {
      setShowNameError(true);
      return;
    }
    save.mutate();
  };

  const suggest = useMutation({
    mutationFn: onSuggest,
    onSuccess: (suggestion) => {
      setName(suggestion.name);
      setDescription(suggestion.description);
      setShowNameError(false);
    },
  });
  const busy = save.isPending || suggest.isPending;

  if (save.isSuccess) {
    const shared = save.data.visibility === PAGE_LIBRARY_VISIBILITY.team;
    return (
      <Modal
        open
        title="Saved to your library"
        onClose={onClose}
        actions={<Button onClick={onClose}>Done</Button>}
      >
        <p>
          <b className="text-portal-ink">{save.data.name}</b> is in your page library
          {shared ? ' and the whole team can use it.' : '. Only you can see it.'} Add it to any proposal from{' '}
          <b className="text-portal-ink">Pages → From library</b>.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      open
      title="Save to library"
      subtitle={librarySummaryLine(pages)}
      onClose={onClose}
      dismissible={!busy}
      className="max-w-lg"
      phoneFullScreen
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            {save.isPending ? 'Saving…' : 'Save to library'}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {save.isError ? (
          <ErrorNotice inline title="Couldn't save these pages." message={errorMessage(save.error)} />
        ) : null}
        {suggest.isError ? (
          <ErrorNotice inline tone="warning" title="Couldn't draft a suggestion." message={errorMessage(suggest.error)} />
        ) : null}
        <Field
          label="Name"
          htmlFor={nameId}
          required
          action={
            <Button variant="ghost" size="compact" className="h-auto px-0" onClick={() => suggest.mutate()} disabled={busy}>
              {suggest.isPending ? 'Suggesting…' : 'Suggest'}
            </Button>
          }
        >
          <TextInput
            id={nameId}
            value={name}
            maxLength={PAGE_LIBRARY_NAME_MAX}
            placeholder="e.g. Giving back: the donation moment"
            aria-invalid={showNameError || undefined}
            onChange={(event) => {
              setName(event.target.value);
              setShowNameError(false);
            }}
            autoFocus
          />
          {showNameError ? (
            <p role="alert" className="mt-1 text-portal-micro text-rose-700">
              Give these pages a name.
            </p>
          ) : null}
        </Field>
        <Field
          label="Description"
          htmlFor={descriptionId}
          hint="People find pages by searching names and descriptions."
        >
          <TextArea
            id={descriptionId}
            rows={3}
            value={description}
            maxLength={PAGE_LIBRARY_DESCRIPTION_MAX}
            placeholder="What these pages are for"
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        <div>
          <p className="mb-1.5 text-portal-tiny font-bold">Who can use these pages</p>
          <SegmentedControl
            label="Who can use these pages"
            options={VISIBILITY_OPTIONS}
            value={visibility}
            onChange={setVisibility}
          />
        </div>
        {/* Enter in the name field submits the form. */}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
